import { useTranslation } from "react-i18next";
import { TrendingUp, TrendingDown, Sparkles } from "lucide-react";

interface TotalBalanceSummaryProps {
  totalReceivable: number;
  totalPayable: number;
}

export function TotalBalanceSummary({ totalReceivable, totalPayable }: TotalBalanceSummaryProps) {
  const { t } = useTranslation();

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  const netBalance = totalReceivable - totalPayable;
  const isEven = totalReceivable === 0 && totalPayable === 0;

  if (isEven) {
    return (
      <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 p-6 text-white shadow-lg shadow-emerald-500/20">
        <div className="flex items-center justify-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
            <Sparkles className="h-6 w-6" />
          </div>
          <div className="text-center">
            <p className="text-lg font-semibold">みんなイーブン！</p>
            <p className="text-sm text-white/80">精算待ちはありません</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Net Balance Summary */}
      <div
        className={`overflow-hidden rounded-2xl p-5 shadow-lg ${
          netBalance >= 0
            ? "bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-500/20"
            : "bg-gradient-to-br from-red-500 to-orange-500 shadow-red-500/20"
        }`}
      >
        <div className="flex items-center justify-between text-white">
          <div>
            <p className="text-sm font-medium text-white/80">
              {netBalance >= 0 ? "トータルで受け取れる" : "トータルで支払う"}
            </p>
            <p className="mt-1 text-3xl font-bold tabular-nums">
              {formatAmount(Math.abs(netBalance))}
            </p>
          </div>
          <div
            className={`flex h-14 w-14 items-center justify-center rounded-full ${
              netBalance >= 0 ? "bg-white/20" : "bg-white/20"
            }`}
          >
            {netBalance >= 0 ? (
              <TrendingUp className="h-7 w-7" />
            ) : (
              <TrendingDown className="h-7 w-7" />
            )}
          </div>
        </div>
      </div>

      {/* Breakdown */}
      <div className="grid grid-cols-2 gap-3">
        {/* To Receive */}
        <div className="rounded-xl border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-950/30">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-500">
              <TrendingUp className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">{t("home.toReceive", "受け取る")}</p>
              <p className="truncate text-lg font-bold tabular-nums text-green-600 dark:text-green-400">
                {formatAmount(totalReceivable)}
              </p>
            </div>
          </div>
        </div>

        {/* To Pay */}
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-500">
              <TrendingDown className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">{t("home.toPay", "支払う")}</p>
              <p className="truncate text-lg font-bold tabular-nums text-red-600 dark:text-red-400">
                {formatAmount(totalPayable)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
